import React, { useState } from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';

import { billingInfoStateForAccountIdSelector } from '../../../../account/account-selectors';
import { updateAccount } from '../../../../account/actions/update-account';
import { loadSalesforceAccountId } from '../../../../salesforce/actions/load-salesforce-account-id';
import { createTwilioProfileAndBrand } from '../../../actions/create-twilio-profile-and-brand';

import { validatePhone } from '../../../../businesslogic/form-validators';
import { UiButton } from '../../../../ui/ui-button';
import { UiDialog } from '../../../../ui/ui-dialog';

import { convertPhoneNumberToE164 } from '../../../../util/formatters';
import { FormStateAutocomplete } from '../../../../widgets/react-hook-form-components/form-state-autocomplete';
import { FormTextField } from '../../../../widgets/react-hook-form-components/form-text-field';

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { account }: AccountStatusTransitionDialogProps,
) => ({
  actions: {
    updateAccount: (updatedAccount: TeamEngineAccountUpdateRequest) =>
      dispatch(updateAccount(account.accountId, updatedAccount)),
    createTwilioProfileAndBrand: () => dispatch(createTwilioProfileAndBrand(account.accountId)),
    loadSalesforceAccountId: (website: string) => dispatch(loadSalesforceAccountId(website)),
  },
});

const mapStateToProps = (state: any, { account }: AccountStatusTransitionDialogProps) => ({
  billingInfoState: billingInfoStateForAccountIdSelector(state, account.accountId),
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = AccountStatusTransitionDialogProps & StateProps & DispatchProps;

export const TrialStartedAccountDialog = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    account,
    org,
    cancelTransition,
    handleTransitionConfirmed,
    billingInfoState,
    actions,
  }: Props) => {
    const [salesforceAccountId, setSalesforceAccountId] = useState<string>(
      account.salesforceId || '',
    );
    const [salesforceAccountIdIsLoading, setSalesforceAccountIdIsLoading] =
      useState<boolean>(false);
    const [salesforceError, setSalesforceError] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const isLoading = billingInfoState?.loading || isSubmitting;
    const { website, name } = org || { website: null, name: null };

    const hasTwilioBrand = Boolean(account.twilioTrustHubBundleSid);

    // At one point we were able to create twilio brands without customer intervention, and this was all the information we needed.
    // Now, we need to create full standard brands for all of our customers. We still want the AEs to enter most of the information so that
    // our customers don't have to, but the in particular the EIN and business type is hard information to discern from the outside, so we
    // are prompting our customers to enter just that piece.
    const hasAllAccountExecRequiredBrandInformation = Boolean(
      (account.businessName || name) &&
        account.businessStreet &&
        account.businessCity &&
        account.businessState &&
        account.businessZipCode &&
        account.businessPhoneNumber,
    );
    const shouldCollectAccountBusinessInformation =
      !hasTwilioBrand && !hasAllAccountExecRequiredBrandInformation && org && !org.internal;

    const defaultValues = {
      businessName: account.businessName || name,
      businessStreet: account.businessStreet || '',
      businessUnit: account.businessUnit || '',
      businessCity: account.businessCity || '',
      businessState: account.businessState || '',
      businessZipCode: account.businessZipCode || '',
      businessPhoneNumber: account.businessPhoneNumber || '',
    };

    const {
      control,
      errors,
      formState: { isDirty },
      handleSubmit,
      reset,
    } = useForm({ mode: 'all', defaultValues });

    const onSubmit: SubmitHandler<typeof defaultValues> = async (update) => {
      setIsSubmitting(true);
      // If this account was just created, it should have all information we want the Account Executives to enter
      if (!hasTwilioBrand && hasAllAccountExecRequiredBrandInformation && !org?.internal) {
        // It is possible that it was made in the (long) period where we didn't default the businessName
        // to the name used for the account and first org - if that's the case, just update the businessName
        // to be the org.name
        if (!account.businessName && name) {
          await actions.updateAccount({
            businessName: name,
          });
        }
        // Otherwise, if we collected business information because some of it was missing, we should update the account with it,
        // and then create the twilio brand
      } else if (shouldCollectAccountBusinessInformation) {
        const {
          businessName,
          businessStreet,
          businessUnit,
          businessCity,
          businessState,
          businessZipCode,
          businessPhoneNumber,
        } = update;
        await actions.updateAccount({
          businessName: businessName || null,
          businessStreet: businessStreet || null,
          businessUnit: businessUnit || null,
          businessCity: businessCity || null,
          businessState: businessState || null,
          businessZipCode: businessZipCode || null,
          businessPhoneNumber:
            businessPhoneNumber && !validatePhone(businessPhoneNumber)
              ? convertPhoneNumberToE164(businessPhoneNumber)
              : null,
        });
        reset({
          ...defaultValues,
          ...update,
        });
      }
      setIsSubmitting(false);
      return handleTransitionConfirmed({ salesforceId: salesforceAccountId });
    };

    const handleFindSalesforceIdClick = async () => {
      setSalesforceAccountIdIsLoading(true);
      const response = website ? await actions.loadSalesforceAccountId(website) : null;
      if (response?.salesforceAccountId) {
        setSalesforceAccountId(response.salesforceAccountId);
        setSalesforceAccountIdIsLoading(false);
        setSalesforceError('');
      } else {
        const attemptedSalesforceAccountName =
          website && new URL(website).hostname.replace('www.', '');
        let errorMessage =
          'To input it manually, go to the account page in Salesforce and look in the URL after "Account/". The ID will be there and look something like "001f400001IWC3VAA"';
        if (attemptedSalesforceAccountName) {
          errorMessage = `No Salesforce account matching "${attemptedSalesforceAccountName}". ${errorMessage}`;
        } else {
          errorMessage = `No account website to look up Salesforce name. ${errorMessage}`;
        }
        setSalesforceError(errorMessage);
        setSalesforceAccountIdIsLoading(false);
      }
    };

    const handleSalesforceAccountIdChange = ({
      target: { value },
    }: React.ChangeEvent<{ value: unknown }>) => {
      setSalesforceError('');
      setSalesforceAccountId(value as string);
    };

    const handleStartTrialClick = () => {
      if (!salesforceAccountId) {
        setSalesforceError('Please enter a Salesforce AccountID');
        return;
      }

      handleSubmit(onSubmit)();
    };

    const shouldStartTrialButtonBeDisabled = Boolean(
      !salesforceAccountId ||
        (shouldCollectAccountBusinessInformation && Object.keys(errors).length) ||
        (!hasAllAccountExecRequiredBrandInformation && !isDirty),
    );

    return (
      <UiDialog title="Start Trial" size="sm" onClose={cancelTransition}>
        <UiDialog.Content>
          <>
            <Paper className="u-padding" variant="outlined">
              <div className="typo-label">Salesforce Information</div>
              <div className="u-display-flex u-alignItems-flexEnd">
                <TextField
                  className="u-width-100"
                  id="salesforceId"
                  label="Salesforce Id"
                  value={salesforceAccountId}
                  onChange={handleSalesforceAccountIdChange}
                  margin="dense"
                  required
                />
                <UiButton
                  size="sm"
                  className="u-margin-left u-flexShrink-0"
                  loading={salesforceAccountIdIsLoading}
                  onClick={handleFindSalesforceIdClick}
                >
                  Find Salesforce ID
                </UiButton>
              </div>
              {salesforceError && (
                <div className="typo-subtitle u-color-red u-margin-top-sm">{salesforceError}</div>
              )}
            </Paper>
            {shouldCollectAccountBusinessInformation ? (
              <Paper className="u-padding u-margin-top" variant="outlined">
                <div className="typo-label">Required Account Information</div>
                <div className="typo-subtitle u-margin-bottom">
                  This information is used to register our customer with the carriers. Please ensure
                  its accuracy
                </div>
                <form>
                  <FormTextField
                    control={control}
                    name="businessName"
                    label="Business Name"
                    error={errors.businessName}
                    required
                  />
                  <FormTextField
                    control={control}
                    name="businessPhoneNumber"
                    label="Business Phone Number"
                    error={errors.businessPhoneNumber}
                    required
                  />
                  <div className="typo-label u-margin-y">Address</div>
                  <div className="u-display-flex">
                    <FormTextField
                      className="u-margin-right"
                      control={control}
                      name="businessStreet"
                      label="Street"
                      error={errors.businessStreet}
                      required
                    />
                    <FormTextField
                      control={control}
                      name="businessUnit"
                      label="Unit"
                      error={errors.businessUnit}
                    />
                  </div>
                  <div className="u-flex-center">
                    <FormTextField
                      className="u-margin-right"
                      control={control}
                      name="businessCity"
                      label="City"
                      error={errors.businessCity}
                      required
                    />
                    <div className="u-width-100 u-margin-x">
                      <FormStateAutocomplete
                        className="u-margin-right"
                        control={control}
                        name="businessState"
                        label="State"
                        error={errors.businessState}
                        required
                      />
                    </div>
                    <FormTextField
                      control={control}
                      name="businessZipCode"
                      label="Zip Code"
                      error={errors.businessZipCode}
                      required
                    />
                  </div>
                </form>
              </Paper>
            ) : null}
          </>
        </UiDialog.Content>
        <UiDialog.Actions>
          <UiButton onClick={cancelTransition} variant="dismiss" disabled={isLoading}>
            Cancel
          </UiButton>
          <UiButton
            onClick={handleStartTrialClick}
            disabled={shouldStartTrialButtonBeDisabled}
            loading={isLoading}
          >
            Start Trial
          </UiButton>
        </UiDialog.Actions>
      </UiDialog>
    );
  },
);
